import type {
    AdminDifficultyCacheBuildResponse,
    AdminDifficultyParamsResponse,
    AdminDifficultyParamsSetResponse,
    AdminDifficultyPreviewResponse,
    AdminPuzzleRoutesResponse,
    AdminPuzzleSummary,
    Hint,
    Puzzle,
    Recap,
    Stats,
} from "./types";

export type PlayResponse = Puzzle & {
    session: string;
};

export const adminDifficultyParams = async (): Promise<AdminDifficultyParamsResponse> => {
    return request<AdminDifficultyParamsResponse>(
        "/admin/difficulty_params",
        {},
        "Failed to load difficulty params",
    );
};

export const adminDifficultyParamsSet = async (
    params: Record<string, unknown>,
): Promise<AdminDifficultyParamsSetResponse> => {
    return request<AdminDifficultyParamsSetResponse>(
        "/admin/difficulty_params_set",
        { params },
        "Failed to save difficulty params",
    );
};

export const adminDifficultyParamsClear = async (): Promise<AdminDifficultyParamsSetResponse> => {
    return request<AdminDifficultyParamsSetResponse>(
        "/admin/difficulty_params_set",
        { clear: true },
        "Failed to clear difficulty params",
    );
};

export const adminDifficultyCacheBuild = async (
    puzzle_ids: number[],
): Promise<AdminDifficultyCacheBuildResponse> => {
    return request<AdminDifficultyCacheBuildResponse>(
        "/admin/difficulty_cache_build",
        { puzzle_ids },
        "Failed to build difficulty cache",
    );
};

export const adminDifficultyPreview = async (
    puzzle_ids: number[],
    params?: Record<string, unknown>,
): Promise<AdminDifficultyPreviewResponse> => {
    return request<AdminDifficultyPreviewResponse>(
        "/admin/difficulty_preview",
        { puzzle_ids, params: params ?? null },
        "Failed to preview difficulty",
    );
};

export type IngestEvent = {
    client_event_id: string;
    session: string;
    seq: number;
    event_type: string;
    elapsed_ms?: number;
    client_ts?: number;
    data?: unknown;
};

export type IngestEventsResponse = {
    accepted: number;
    ignored_duplicates: number;
    ignored_unknown_sessions: number;
    ignored_invalid: number;
};

const request = async <T>(
    path: string,
    body: unknown,
    fallbackMessage: string,
): Promise<T> => {
    const res = await fetch(`${import.meta.env.VITE_API_ENDPOINT}${path}`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            mode: "cors",
            body: JSON.stringify(body),
        },
    );

    let data: unknown = null;
    try {
        data = await res.json();
    } catch {
        // noop: non-JSON or empty body
    }

    if (!res.ok) {
        const message =
            data && typeof (data as any).error === "string" && (data as any).error
                ? (data as any).error
                : fallbackMessage;
        throw new Error(message);
    }

    return data as T;
};

export const play = async (date: string): Promise<PlayResponse> => {
    return request<PlayResponse>("/play", { date }, "Failed to start attempt");
};

export const solve = async (
    session: string,
    solution: string,
    time: number,
): Promise<Stats> => {
    return request<Stats>(
        "/solve",
        { session, solution, time },
        "Failed to submit solution",
    );
};

export const recap = async (
    puzzle_id: number,
    session: string,
): Promise<Recap> => {
    return request<Recap>(
        "/recap",
        { puzzle_id, session },
        "Failed to load recap",
    );
};

export const adminPuzzleRoutes = async (
    puzzle_id: number,
    limit_attempts: number = 500,
    only_solved: boolean = false,
): Promise<AdminPuzzleRoutesResponse> => {
    return request<AdminPuzzleRoutesResponse>(
        "/admin/puzzle_routes",
        { puzzle_id, limit_attempts, only_solved },
        "Failed to load puzzle routes",
    );
};

export const hint = async (
    session: string,
    kings: Array<number>,
    forbidden: Array<number>,
): Promise<Hint> => {
    return request<Hint>(
        "/hint",
        { session, kings, forbidden },
        "Failed to fetch hint",
    );
};

export const rate = async (
    session: string,
    rating: number,
): Promise<void> => {
    // We don't currently use the response body; errors are surfaced via thrown exceptions.
    await request<unknown>(
        "/rate",
        { session, rating },
        "Failed to submit rating",
    );
};

export type AdminPuzzlesResponse = {
    items: AdminPuzzleSummary[];
    total: number;
    limit: number;
    offset: number;
};

export const adminPuzzles = async (
    limit: number = 200,
    offset: number = 0,
): Promise<AdminPuzzlesResponse> => {
    return request<AdminPuzzlesResponse>(
        "/admin/puzzles",
        { limit, offset },
        "Failed to load puzzles",
    );
};

export const ingestEvents = async (events: IngestEvent[]): Promise<IngestEventsResponse> => {
    return request<IngestEventsResponse>(
        "/events",
        { events },
        "Failed to send events",
    );
};
